import { BehaviorSubject } from 'rxjs';
import { take } from 'rxjs/operators';
import { EChangeOperation, IHistoryStateChange, isTrackedClass, ITrackedClass } from '../interfaces/tracked-class';
import { changesProcessed } from './utilities';

type TrackedObject = ITrackedClass | any[];
type ChangeGroup = {
    change: Map<TrackedObject, IHistoryStateChange>;
    undoFunction?: () => void;
    redoFunction?: () => void;
    description: string;
};

export interface ChangeInfo {
    index: number;
    description: string;
    nodes: any[];
    properties: IterableIterator<string | number | symbol>;
}

class SceneHistory {
    private active = false;
    private changes: ChangeGroup[] = [];
    private undoneChanges: ChangeGroup[] = [];

    private ignoreChangesFromObjects = new Set<TrackedObject>();

    openChange: ChangeGroup | undefined;
    state = new BehaviorSubject<ChangeInfo[]>([]);
    undoneState = new BehaviorSubject<ChangeInfo[]>([]);

    private updateState() {
        return this.state.next(this.getMergedChanges(this.changes));
    }
    private updateUndoneState() {
        return this.undoneState.next(this.getMergedChanges(this.undoneChanges));
    }

    private getMergedChanges(changes: ChangeGroup[]) {
        return changes.map((c, index) => {
            const changePart = Array.from(c.change.values());
            return {
                description: c.description,
                index,
                nodes: changePart.map(node => node.node),
                // Return a set of properties to drop duplicate array elements
                properties: new Set(
                    changePart.flatMap(node => node.properties.flatMap(property => property.name))
                ).values(),
            };
        });
    }

    ignoreUntilStable(object: TrackedObject) {
        if (this.ignoreChangesFromObjects.size === 0) {
            changesProcessed.pipe(take(1)).subscribe(() => {
                this.ignoreChangesFromObjects.clear();
            });
        }
        this.ignoreChangesFromObjects.add(object);
    }

    start() {
        this.active = true;
    }

    stop() {
        this.active = false;
    }

    /**
     * Adds a change to the history. If you added an open-ended change
     * @param description
     * @param processFunction
     * @param undoFunction
     */
    addChange(description: string, processFunction: (redo: boolean) => void, undoFunction?: () => void) {
        // if (this.openChange) {
        //     console.warn(
        //         'Starting a new history change with a previous change still open. You may get a strange history state.',
        //         `Change still open: ${this.openChange.description}`,
        //         `New change: ${description}`
        //     );
        // }

        let newChange: ChangeGroup | undefined;
        if (!this.openChange) {
            newChange = this.openChange = {
                change: new Map(),
                description,
                undoFunction,
            };
        }
        processFunction(false);

        const done = (description: string | void) => {
            if (this.openChange) {
                if (this.openChange.change.size > 0 || this.openChange.undoFunction !== undefined) {
                    if (description) {
                        this.openChange.description = description;
                    }
                    this.changes.push(this.openChange);
                }

                delete this.openChange;
                this.updateState();

                if (this.undoneChanges.length > 0) {
                    this.undoneChanges.clear();
                    this.updateUndoneState();
                }
            }
        };

        if (undoFunction) {
            this.openChange.redoFunction = () => processFunction(true);
            done();
        } else if (newChange) {
            changesProcessed.pipe(take(1)).subscribe(done);
        }
    }

    undo() {
        if (this.openChange) {
            throw Error("Can't undo while there are open changes");
        }
        if (this.changes.length < 1) {
            return;
        }
        const lastChangeSet = this.changes.pop();
        if (lastChangeSet) {
            this.undoneChanges.push(lastChangeSet);
            if (lastChangeSet.undoFunction) {
                lastChangeSet.undoFunction();
            } else {
                this.processChangeSet(lastChangeSet, change => {
                    // Undo changes in reverse so they are consistent with how they were added.
                    change.properties
                        .slice()
                        .reverse()
                        .forEach(property => {
                            const restore = property.previousValue;
                            switch (property.operation) {
                                case EChangeOperation.CreateAndPush:
                                    change.node.pop(true);
                                    break;
                                case EChangeOperation.Push:
                                    change.node.pop();
                                    break;
                                case EChangeOperation.Unshift:
                                    change.node.shift();
                                    break;
                                case EChangeOperation.Shift:
                                    change.node.unshift(property.previousValue);
                                    if (isTrackedClass(restore)) {
                                        (restore as ITrackedClass).isModified = true;
                                    }
                                    break;
                                case EChangeOperation.Pop:
                                    change.node.push(property.previousValue);
                                    if (isTrackedClass(restore)) {
                                        (restore as ITrackedClass).isModified = true;
                                    }
                                    break;
                                case EChangeOperation.Remove:
                                case EChangeOperation.Splice:
                                    // Property name is the index of the splice
                                    // Remove all the added values, add back in the old ones
                                    change.node.splice(property.name, property.newValue.length, ...restore);
                                    if (Array.isArray(restore) && restore.length > 0) {
                                        restore.forEach(restoreItem => {
                                            if (isTrackedClass(restoreItem)) {
                                                (restoreItem as ITrackedClass).isModified = true;
                                            }
                                        });
                                    }
                                    break;
                                default:
                                    change.node[property.name] = property.previousValue;
                            }
                        });
                });
            }

            this.updateState();
            this.updateUndoneState();
        }
    }

    redo() {
        if (this.openChange) {
            throw Error("Can't redo while there are open changes");
        }
        if (this.undoneChanges.length < 1) {
            return;
        }
        const lastUndoneChangeSet = this.undoneChanges.pop();
        if (lastUndoneChangeSet) {
            this.changes.push(lastUndoneChangeSet);
            if (lastUndoneChangeSet.redoFunction) {
                lastUndoneChangeSet.redoFunction();
            } else {
                this.processChangeSet(lastUndoneChangeSet, change => {
                    // Redo changes in the initial order
                    change.properties.forEach(property => {
                        const restore = property.newValue;
                        switch (property.operation) {
                            case EChangeOperation.CreateAndPush:
                                change.node.createAndPush(property.newValue);
                                if (isTrackedClass(restore)) {
                                    (restore as ITrackedClass).isModified = true;
                                }
                                break;
                            case EChangeOperation.Push:
                                change.node.push(property.newValue);
                                break;
                            case EChangeOperation.Unshift:
                                change.node.unshift(property.newValue);
                                if (isTrackedClass(restore)) {
                                    (restore as ITrackedClass).isModified = true;
                                }
                                break;
                            case EChangeOperation.Shift:
                                change.node.shift();
                                break;
                            case EChangeOperation.Pop:
                                change.node.pop();
                                break;
                            case EChangeOperation.Remove:
                                property.previousValue.length === 1 && change.node.remove(property.previousValue[0]);
                                break;
                            case EChangeOperation.Splice:
                                change.node.splice(property.name, property.previousValue.length, ...restore);
                                if (Array.isArray(restore) && restore.length > 0) {
                                    restore.forEach(restoreItem => {
                                        if (isTrackedClass(restoreItem)) {
                                            (restoreItem as ITrackedClass).isModified = true;
                                        }
                                    });
                                }
                                break;
                            default:
                                change.node[property.name] = property.newValue;
                        }
                    });
                });
            }

            this.updateState();
            this.updateUndoneState();
        }
    }

    undoTo(index: number) {
        while (this.changes.length > index + 1) {
            this.undo();
        }
    }

    redoTo(index: number) {
        while (this.undoneChanges.length > index) {
            this.redo();
        }
    }

    // startGroupingAllChanges() {
    //     this.groupAllIncomingChanges = true;
    //     this.changes.push(new Map());
    // }

    // stopGroupingAllChanges() {
    //     this.groupAllIncomingChanges = false;
    //     this.removeLastChangeIfEmpty();
    //     this.updateState();
    // }

    // startGroupingObjectsChanges(objects: Array<Object>) {
    //     this.groupChangesInObjects = new Set(objects.filter<TrackedObject>(isTrackedClass));
    //     this.changes.push(new Map());
    // }

    // stopGroupingObjectsChanges() {
    //     this.groupChangesInObjects = undefined;
    //     this.removeLastChangeIfEmpty();
    // }

    changeEvent<T>(
        object: T,
        property: keyof T | symbol,
        value: any,
        originalValue: any,
        operation?: EChangeOperation
    ) {
        if (
            this.active &&
            (isTrackedClass(object) || Array.isArray(object)) &&
            value !== originalValue &&
            !this.ignoreChangesFromObjects.has(object)
        ) {
            if (!this.openChange) {
                return;
            }
            const openChange = this.openChange;

            let lastChangeOnObject = openChange.change.get(object);
            if (!lastChangeOnObject) {
                lastChangeOnObject = { node: object, properties: [] };
                openChange.change.set(object, lastChangeOnObject);
            }

            const lastChangeOnObjectProperty = lastChangeOnObject.properties.find(
                openChange => openChange.name === property
            );
            if (!lastChangeOnObjectProperty) {
                lastChangeOnObject.properties.push({
                    name: property,
                    previousValue: originalValue,
                    newValue: value,
                    operation,
                });
            } else {
                lastChangeOnObjectProperty.newValue = value;
            }
        }
    }

    changeEvents<T>(
        object: T,
        changes: Array<{ property: keyof T | symbol; value: any; originalValue: any; operation: EChangeOperation }>
    ) {
        if (
            this.active &&
            (isTrackedClass(object) || Array.isArray(object)) &&
            !this.ignoreChangesFromObjects.has(object)
        ) {
            changes.forEach(change => {
                this.changeEvent(object, change.property, change.value, change.originalValue, change.operation);
            });
        }
    }

    removeLastChange<T>(object: T, property: keyof T) {
        // todo
    }

    private processChangeSet(changeSet: ChangeGroup, process: (change: IHistoryStateChange) => void) {
        changeSet.change.forEach(change => process(change));
    }
}

export const history = new SceneHistory();
